import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CategoryTable = ({ 
  categories = [], 
  onEdit, 
  onDelete, 
  searchTerm = '',
  levelFilter = 'all',
  sortConfig = { key: null, direction: 'asc' },
  onSort,
  selectedCategories = [],
  onSelectCategory,
  onSelectAll
}) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Filter and sort categories
  const filteredAndSortedCategories = useMemo(() => {
    let filtered = categories?.filter(category => {
      const matchesSearch = category?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           category?.parentCategory?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesLevel = levelFilter === 'all' || category?.level?.toString() === levelFilter;
      return matchesSearch && matchesLevel;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];
        
        if (sortConfig?.key === 'level') {
          aValue = parseInt(aValue);
          bValue = parseInt(bValue);
        }
        
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [categories, searchTerm, levelFilter, sortConfig]);

  const handleSort = (key) => {
    onSort(key);
  };

  const handleDragStart = (e, category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, category) => {
    e?.preventDefault();
    setDragOverItem(category);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, targetCategory) => {
    e?.preventDefault();
    if (draggedItem && draggedItem?.id !== targetCategory?.id) {
      // Handle reordering logic here
      console.log('Reorder:', draggedItem, 'to position of', targetCategory);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const getLevelIndentation = (level) => {
    return `${(level - 1) * 20}px`;
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const isAllSelected = selectedCategories?.length === filteredAndSortedCategories?.length && filteredAndSortedCategories?.length > 0;
  const isIndeterminate = selectedCategories?.length > 0 && selectedCategories?.length < filteredAndSortedCategories?.length;

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={input => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  className="rounded border-border focus:ring-2 focus:ring-primary"
                />
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted transition-colors"
                onClick={() => handleSort('serialNo')}
              >
                <div className="flex items-center space-x-2">
                  <span>S.No</span>
                  <Icon name={getSortIcon('serialNo')} size={14} />
                </div>
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-2">
                  <span>Category</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </div>
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted transition-colors"
                onClick={() => handleSort('parentCategory')}
              >
                <div className="flex items-center space-x-2">
                  <span>Parent Category</span>
                  <Icon name={getSortIcon('parentCategory')} size={14} />
                </div>
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted transition-colors"
                onClick={() => handleSort('level')}
              >
                <div className="flex items-center space-x-2">
                  <span>Category Level</span>
                  <Icon name={getSortIcon('level')} size={14} />
                </div>
              </th>
              <th className="text-right px-4 py-3 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedCategories?.map((category, index) => (
              <tr
                key={category?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, category)}
                onDragOver={(e) => handleDragOver(e, category)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, category)}
                className={`
                  border-b border-border hover:bg-muted/30 transition-colors cursor-move
                  ${dragOverItem?.id === category?.id ? 'bg-accent/10 border-accent' : ''}
                  ${selectedCategories?.includes(category?.id) ? 'bg-primary/5' : ''}
                `}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedCategories?.includes(category?.id)}
                    onChange={(e) => onSelectCategory(category?.id, e?.target?.checked)}
                    className="rounded border-border focus:ring-2 focus:ring-primary"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-foreground font-medium">
                  {category?.serialNo}
                </td>
                <td className="px-4 py-3">
                  <div 
                    className="flex items-center space-x-2"
                    style={{ paddingLeft: getLevelIndentation(category?.level) }}
                  >
                    {category?.level > 1 && (
                      <Icon name="CornerDownRight" size={14} className="text-muted-foreground" />
                    )}
                    <span className="text-sm font-medium text-foreground">{category?.name}</span>
                    <span className="px-2 py-1 bg-muted text-xs rounded-full text-muted-foreground">
                      L{category?.level}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {category?.parentCategory || '-'}
                </td>
                <td className="px-4 py-3">
                  <span className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    ${category?.level === 1 ? 'bg-primary/10 text-primary' : 
                      category?.level === 2 ? 'bg-accent/10 text-accent': 'bg-warning/10 text-warning'}
                  `}>
                    Level {category?.level}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEdit(category)}
                      className="text-muted-foreground hover:text-foreground"
                    />
                    {/* <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDelete(category)}
                      className="text-muted-foreground hover:text-destructive"
                    /> */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3 p-4">
        {filteredAndSortedCategories?.map((category) => (
          <div
            key={category?.id}
            className={`
              bg-card border border-border rounded-lg p-4 space-y-3
              ${selectedCategories?.includes(category?.id) ? 'border-primary bg-primary/5' : ''}
            `}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedCategories?.includes(category?.id)}
                  onChange={(e) => onSelectCategory(category?.id, e?.target?.checked)}
                  className="rounded border-border focus:ring-2 focus:ring-primary mt-1"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-foreground">{category?.name}</h3>
                    <span className="px-2 py-1 bg-muted text-xs rounded-full text-muted-foreground">
                      #{category?.serialNo}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Parent: {category?.parentCategory || 'None'}
                  </p>
                </div>
              </div>
              <span className={`
                px-2 py-1 rounded-full text-xs font-medium
                ${category?.level === 1 ? 'bg-primary/10 text-primary' : 
                  category?.level === 2 ? 'bg-accent/10 text-accent': 'bg-warning/10 text-warning'}
              `}>
                Level {category?.level}
              </span>
            </div>
            
            <div className="flex items-center justify-end space-x-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => onEdit(category)}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                onClick={() => onDelete(category)}
                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {filteredAndSortedCategories?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No categories found</h3>
          <p className="text-muted-foreground">
            {searchTerm || levelFilter !== 'all' ?'Try adjusting your search or filter criteria' :'Create your first category to get started'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default CategoryTable;